import {
  EuiButtonEmpty,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFlyout,
  EuiFlyoutBody,
  EuiFlyoutHeader,
  EuiPanel,
  EuiSpacer,
  EuiText,
  EuiTitle,
} from '@elastic/eui';
import type { MouseEvent } from 'react';
import { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import type { Fund, FundAssets, FundProfile } from '../model';
import { getDomicileURL, isFundProfile } from '../model';
import { PageContext } from '../page_container';
import { FundInfoPanel } from './fund_info_panel';
import { FundAssetsTable } from '../pages/fund';

export function FundDetailsFlyout({
  fundOrProfile,
  onClose,
}: {
  fundOrProfile: Fund | FundProfile;
  onClose: () => void;
}) {
  const navigate = useNavigate();

  const [fundAssets, setFundAssets] = useState<FundAssets | null>(null);
  const { domicileCode } = useContext(PageContext);
  const fundId = isFundProfile(fundOrProfile) ? fundOrProfile.id : fundOrProfile.isin;

  const asOfDate = useMemo(() => {
    if (!fundAssets || !fundAssets.asOfDate) {
      return ' ';
    }

    return `As of ${new Date(fundAssets.asOfDate).toLocaleDateString()}`;
  }, [fundAssets]);

  const fundUrl = getDomicileURL(domicileCode, `/fund/${fundId}`);

  return (
    <EuiFlyout size="m" onClose={() => onClose()} ownFocus={true}>
      <EuiFlyoutHeader>
        <EuiTitle size="s" textTransform="uppercase">
          <EuiFlexGroup alignItems={'center'} gutterSize="s">
            <EuiFlexItem>
              <h1>{fundOrProfile.name}</h1>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiText size={'xs'} color={'subdued'}>
                {asOfDate}
              </EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiTitle>
      </EuiFlyoutHeader>
      <EuiFlyoutBody>
        <FundInfoPanel fundOrProfile={fundOrProfile} />
        <EuiPanel paddingSize="s" color="subdued">
          <EuiButtonEmpty
            aria-label={'View as page'}
            iconType="document"
            color="primary"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              navigate(fundUrl);
              onClose();
            }}
            title={'View as page'}
            href={fundUrl}
          >
            View as page
          </EuiButtonEmpty>
        </EuiPanel>
        <EuiSpacer />
        <FundAssetsTable fundOrProfile={fundOrProfile} onAssetsLoaded={setFundAssets} />
      </EuiFlyoutBody>
    </EuiFlyout>
  );
}
